import { useMemo } from "react";
import { isDeviceOnline } from "../utils/deviceStatus";

/**
 * Custom hook to compute device statistics
 */
export function useDeviceStats(devices) {
  return useMemo(() => {
    if (!devices || devices.length === 0) {
      return { total: 0, online: 0, offline: 0, onlinePercent: 0 };
    }

    const total = devices.length;
    let online = 0;

    for (let index = 0; index < total; index += 1) {
      if (isDeviceOnline(devices[index])) {
        online += 1;
      }
    }

    const offline = total - online;
    const rawPercent = total > 0 ? (online / total) * 100 : 0;
    const onlinePercent =
      rawPercent % 1 === 0 ? rawPercent.toString() : rawPercent.toFixed(1);

    return { total, online, offline, onlinePercent };
  }, [devices]);
}

/**
 * Custom hook to compute recent alerts from Redux state
 */
export function useRecentAlerts(organisationAlerts, devices) {
  return useMemo(() => {
    if (!organisationAlerts || !Array.isArray(organisationAlerts)) return [];

    const devicesById = new Map(
      (devices || []).map((device) => [device.id, device])
    );

    const processedAlerts = organisationAlerts
      .map((alert) => {
        const device = alert.device_id
          ? devicesById.get(alert.device_id)
          : null;

        return {
          ...alert,
          deviceName:
            device?.name || (alert.device_id ? "Unknown Device" : null),
          deviceId: alert.device_id,
          // Ensure we have required fields with fallbacks
          time_created: alert.time_created,
          message: alert.message || alert.action || "Alert triggered",
          severity: alert.severity || alert.level || "info",
        };
      })
      .filter((alert) => alert.time_created)
      .sort((a, b) => Date.parse(b.time_created) - Date.parse(a.time_created))
      .slice(0, 3);

    return processedAlerts;
  }, [organisationAlerts, devices]);
}

/**
 * Custom hook to check if user can add devices
 */
export function useCanAddDevice(
  selectedOrganisation,
  userDB,
  userOrganisationRoles,
  hasPermission
) {
  return useMemo(
    () =>
      selectedOrganisation?.owner_id === userDB?.id ||
      hasPermission(userOrganisationRoles, userDB?.id, "add"),
    [selectedOrganisation, userDB, userOrganisationRoles, hasPermission]
  );
}

/**
 * Custom hook to determine if data is loaded
 */
export function useIsDataLoaded(devices, organisationAlerts) {
  return useMemo(() => {
    return devices !== null && organisationAlerts !== null;
  }, [devices, organisationAlerts]);
}
